import { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import './settings.scss';
import { FaCamera, FaEye, FaEyeSlash, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { PhoneInput } from '../phone-input/PhoneInput';

export function Settings() {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState(user?.user_metadata?.firstName || '');
  const [lastName, setLastName] = useState(user?.user_metadata?.lastName || '');
  const [username, setUsername] = useState(user?.user_metadata?.username || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.user_metadata?.avatar_url || '');
  const [phone, setPhone] = useState(user?.user_metadata?.phone || '+234');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const uploadAvatar = async (file: File) => {
    try {
      // For now, we'll use a temporary URL until storage is fixed
      const tempUrl = URL.createObjectURL(file);

      // Store the avatar in user metadata
      const { error } = await supabase.auth.updateUser({
        data: {
          avatar_url: tempUrl
        }
      });

      if (error) throw error;
      return tempUrl;
    } catch (error) {
      console.error('Error handling avatar:', error);
      throw error;
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const publicUrl = await uploadAvatar(file);
      setAvatarUrl(publicUrl);
      setMessage('Profile picture updated successfully!');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Error updating profile picture');
    } finally {
      setIsUploading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage('');

    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          firstName,
          lastName,
          username,
          avatar_url: avatarUrl,
          phone
        }
      });

      if (error) throw error;
      setMessage('Profile updated successfully');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage('');

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      setMessage('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Error updating password');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button
          className="back-button"
          onClick={() => navigate('/')}
        >
          <FaArrowLeft />
          Back
        </button>
        <h2>Profile Settings</h2>
      </div>

      {message && (
        <div className={`message ${message.includes('error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="avatar-section">
        <div
          className={`avatar-wrapper ${isUploading ? 'uploading' : ''}`}
          onClick={handleAvatarClick}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="Profile" className="avatar" />
          ) : (
            <div className="avatar-placeholder">
              <FaCamera />
            </div>
          )}
          <div className="avatar-overlay">
            {isUploading ? (
              <div className="spinner"></div>
            ) : (
              <FaCamera />
            )}
          </div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </div>

      <form onSubmit={handleProfileUpdate}>
        <div className="form-section">
          <h3>Profile Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <PhoneInput
              id="settingsPhone"
              value={phone}
              onChange={setPhone}
            />
          </div>
        </div>

        <button
          type="submit"
          className="save-button"
          disabled={isSaving}
        >
          {isSaving ? 'Updating...' : 'Update Profile'}
        </button>
      </form>

      <div className="section-divider">
        <span>Password Settings</span>
      </div>

      <form onSubmit={handlePasswordChange} className="password-form">
        <div className="form-group">
          <label htmlFor="currentPassword">Current Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="newPassword">New Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
        </div>

        <button
          type="submit"
          className="save-button"
          disabled={isSaving}
        >
          {isSaving ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </div>
  );
} 